import React, { useEffect, useRef } from 'react';
import sourceColorMap from './utils/sourceColorMap';

const deviceLabel = {
  desktop: '💻',
  mobile: '📱',
  tablet: '📟',
  unknown: '❔',
};

export default function GalaxyGateCosmic({
  liveCount,
  unique24h,
  uniqueAllTime,
  trafficSources,
  regionStats,
  deviceStats,
  surgeDetected,
  lastArrivals,
  recentEvents,
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  const timeAgo = (ts) => {
    if (!ts) return '';
    const delta = Date.now() - ts;
    const sec = Math.floor(delta / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let running = true;
    let last = performance.now();
    const lowPower =
      typeof navigator !== 'undefined' &&
      typeof navigator.hardwareConcurrency === 'number' &&
      navigator.hardwareConcurrency < 4;
    const maxParticles = lowPower ? 120 : 240;

    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resize();
    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    const spawn = () => {
      const w = canvas.width;
      const h = canvas.height;
      const srcKeys = Object.keys(trafficSources || {});
      const src =
        srcKeys[Math.floor(Math.random() * srcKeys.length)] || 'unknown';
      const color = sourceColorMap[src] || sourceColorMap.unknown;
      const speed = 0.4 + Math.random() * 0.8;
      const angle = (Math.random() * Math.PI) / 1.2 - Math.PI / 2.4;
      particlesRef.current.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        color,
      });
      if (particlesRef.current.length > maxParticles) {
        particlesRef.current.shift();
      }
    };

    // kick off some particles
    for (let i = 0; i < 40; i++) spawn();

    const loop = (ts) => {
      if (!running) return;
      const dt = ts - last;
      if (dt < 1000 / 30) {
        animationRef.current = requestAnimationFrame(loop);
        return;
      }
      last = ts;
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = 'rgba(5,8,20,0.55)';
      ctx.fillRect(0, 0, w, h);

      // surge pulse
      if (surgeDetected) {
        const grad = ctx.createRadialGradient(
          w * 0.5,
          h * 0.5,
          10,
          w * 0.5,
          h * 0.5,
          Math.max(w, h) * 0.6,
        );
        grad.addColorStop(0, 'rgba(255,128,255,0.2)');
        grad.addColorStop(1, 'rgba(255,128,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      particlesRef.current.forEach((p) => {
        p.x += p.vx * dt * 0.06;
        p.y += p.vy * dt * 0.06;
        p.life -= 0.002 * dt;
        if (p.x < 0) p.x += w;
        if (p.x > w) p.x -= w;
        if (p.y < 0) p.y += h;
        if (p.y > h) p.y -= h;
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.2;
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - p.vx * 4, p.y - p.vy * 4);
        ctx.stroke();
      });
      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(loop);
    };
    animationRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [trafficSources, surgeDetected]);

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 16,
        minHeight: 220,
        background: 'radial-gradient(circle at 20% 20%, #162040, #0a0f23)',
        color: '#eaf2ff',
        padding: 16,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      <div style={{ position: 'relative', zIndex: 2, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge label="Visitors (live)" value={liveCount ?? 0} color="var(--gp-guest, #78c8ff)" />
          <Badge label="Visitors 24h" value={unique24h ?? 0} color="var(--gp-guest, #78c8ff)" />
          <Badge label="Visitors all-time" value={uniqueAllTime ?? 0} color="var(--gp-guest, #78c8ff)" />
          <span style={{ fontSize: 12, opacity: 0.75 }}>At the Galaxy Gate</span>
        </div>

        <Section title="Top sources">
          {Object.entries(trafficSources || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([src, cnt]) => (
              <Row key={src} label={src} value={cnt} color={sourceColorMap[src] || sourceColorMap.unknown} />
            ))}
          {!trafficSources || !Object.keys(trafficSources).length ? <Muted>No source data</Muted> : null}
        </Section>

        <Section title="Regions">
          {Object.entries(regionStats || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([reg, cnt]) => <Row key={reg} label={reg} value={cnt} />)}
          {!regionStats || !Object.keys(regionStats).length ? <Muted>No region data</Muted> : null}
        </Section>

        <Section title="Devices">
          {Object.entries(deviceStats || {}).map(([dev, cnt]) => (
            <Row
              key={dev}
              label={`${deviceLabel[dev] || '❔'} ${dev}`}
              value={cnt}
            />
          ))}
          {!deviceStats || !Object.keys(deviceStats).length ? <Muted>No device data</Muted> : null}
        </Section>

        <Section title="Activity">
          {recentEvents?.length ? (
            <Sparkline events={recentEvents} />
          ) : (
            <Muted>No activity yet</Muted>
          )}
        </Section>

        <Section title="Last arrivals">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
              gap: 8,
            }}
          >
            {lastArrivals?.slice(0, 6).map((a) => (
              <div
                key={a.sessionId}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                title={a.fullId || a.sessionId}
              >
                <div style={{ fontWeight: 700 }}>{a.label}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {a.source || 'unknown'} · {a.region || 'unknown'}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  Referrer: {a.referrer || 'unknown'}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span>
                    {deviceLabel[a.device] || deviceLabel.unknown}
                    {a.device ? ` ${a.device}` : ''}
                  </span>
                  <span>· {timeAgo(a.at)}</span>
                </div>
              </div>
            ))}
            {!lastArrivals?.length ? <Muted>No arrivals yet</Muted> : null}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Badge({ label, value, color }) {
  return (
    <div
      style={{
        padding: '6px 10px',
        borderRadius: 10,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.1)',
        color,
        fontWeight: 700,
      }}
    >
      <span style={{ opacity: 0.8, color: '#eaf2ff' }}>{label}: </span>
      <span>{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginBottom: 4,
      }}
    >
      {color && (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: color,
          }}
        />
      )}
      <span style={{ textTransform: 'capitalize' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function Muted({ children }) {
  return <div style={{ fontSize: 12, opacity: 0.7 }}>{children}</div>;
}

function Sparkline({ events }) {
  const data = (events || []).filter(
    (d) => Number.isFinite(d?.t) && Number.isFinite(d?.v),
  );
  if (!data.length) return null;
  const minT = data[0].t;
  const maxT = data[data.length - 1].t;
  const span = Math.max(maxT - minT, 1);
  const maxV = Math.max(...data.map((d) => d.v), 1);
  const points = data
    .map((d) => {
      const x = ((d.t - minT) / span) * 100;
      const y = 100 - (d.v / maxV) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 100 100" style={{ width: '100%', height: 60 }}>
      <polyline
        fill="none"
        stroke="#5cffb0"
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
}
